import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App, Button, Card, Dropdown, Input, Modal, Progress, Segmented, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { CopyOutlined, DeleteOutlined, EditOutlined, EllipsisOutlined, PlusOutlined, SendOutlined } from '@ant-design/icons';
import { NameCell, TableSearch } from '../../components/NameCell';
import type { CampaignItem } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { errorMessage } from '../../api/client';
import { pct, useCampaigns, useDeleteCampaign, useDuplicateCampaign, useUpdateCampaign } from '../../features/campaigns/api';
import { EmptyState, tableLoading, tableLocale } from '../../components/states';
import { fmtDateTime } from '../../utils/format';
import { useProjectColumn } from '../../components/ProjectSelect';

/** Why a sending campaign is waiting for its domain, and until when. */
export function heldText(c: { heldReason: string | null; heldUntil: string | null }) {
  return `${c.heldReason ?? 'Waiting for its domain'}. ${c.heldUntil ? `Carries on ${fmtDateTime(c.heldUntil)}.` : 'Carries on when sending is resumed on Tools → Email warm-up.'}`;
}

export const STATUS_COLOR: Record<string, string> = { DRAFT: 'default', SCHEDULED: 'blue', VERIFYING: 'purple', SENDING: 'processing', SENT: 'success', CANCELLED: 'default', FAILED: 'error' };

/** "SCHEDULED" → "Scheduled", "NO_EMAIL" → "No email". */
export const statusLabel = (s: string) => (s.charAt(0) + s.slice(1).toLowerCase()).replace(/_/g, ' ');

export function CampaignsPage() {
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const campaigns = useCampaigns();
  const duplicate = useDuplicateCampaign();
  const remove = useDeleteCampaign();
  const update = useUpdateCampaign();
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const saveName = () => {
    if (!renaming?.name.trim()) return;
    update
      .mutateAsync({ id: renaming.id, name: renaming.name.trim() })
      .then(() => {
        message.success('Campaign renamed');
        setRenaming(null);
      })
      .catch((e) => message.error(errorMessage(e)));
  };
  const projectColumn = useProjectColumn<CampaignItem>();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<'all' | 'draft' | 'scheduled' | 'sent'>('all');
  const rows = useMemo(() => {
    const needle = q.toLowerCase();
    return (campaigns.data ?? []).filter((c) => {
      if (needle && !`${c.name} ${c.subject}`.toLowerCase().includes(needle)) return false;
      if (status === 'draft') return c.status === 'DRAFT';
      if (status === 'scheduled') return ['SCHEDULED', 'VERIFYING', 'SENDING'].includes(c.status);
      if (status === 'sent') return ['SENT', 'CANCELLED', 'FAILED'].includes(c.status);
      return true;
    });
  }, [campaigns.data, q, status]);
  const filtered = !!q || status !== 'all';
  return (
    <div className="page">
      <PageHeader title="Campaigns" subtitle="Email sends to segments, lists, tags or filtered audiences, sent as one of your agents." extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/campaigns/new')}>New campaign</Button>} />
      <Card>
      <div className="table-toolbar">
        <div className="toolbar-group">
          <TableSearch value={q} onSearch={setQ} placeholder="Search campaigns" />
        </div>
        <div className="toolbar-group">
          <Segmented
            value={status}
            onChange={(v) => setStatus(v as typeof status)}
            options={[{ value: 'all', label: 'All' }, { value: 'draft', label: 'Drafts' }, { value: 'scheduled', label: 'Scheduled' }, { value: 'sent', label: 'Sent' }]}
          />
        </div>
      </div>
      <Table<CampaignItem>
        rowKey="id"
        className="table-tight"
        loading={tableLoading(campaigns)}
        locale={tableLocale(
          campaigns,
          filtered ? (
            <EmptyState description="No campaigns match this search or filter." action={<Button onClick={() => { setQ(''); setStatus('all'); }}>Clear search and filter</Button>} />
          ) : (
            <EmptyState description="No campaigns yet. Pick an audience, write the email and send or schedule it." action={<Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/campaigns/new')}>New campaign</Button>} />
          ),
        )}
        dataSource={rows}
        pagination={false}
        // Sized to fit a 1366px laptop beside the expanded sidebar; long names wrap instead of widening the table
        scroll={{ x: 980 }}
        tableLayout="fixed"
        columns={[
          { title: 'Campaign', dataIndex: 'name', width: 200, render: (v: string, c) => <NameCell icon={<SendOutlined />} size={32} lines={3} name={v} sub={c.subject || 'No subject yet'} to={`/campaigns/${c.id}`} /> },
          ...projectColumn.map((col) => ({ ...col, width: 90 })),
          {
            title: 'Status',
            dataIndex: 'status',
            width: 96,
            render: (s: string, c) =>
              s === 'SENDING' && c.heldAt ? (
                <Tooltip title={heldText(c)}><Tag color="warning">Waiting</Tag></Tooltip>
              ) : (
                <Tooltip title={c.error ?? undefined}><Tag color={STATUS_COLOR[s]}>{statusLabel(s)}</Tag></Tooltip>
              ),
          },
          { title: 'Agent', key: 'agent', width: 120, render: (_, c) => c.agent?.name ?? '—' },
          { title: 'Recipients', dataIndex: 'recipientCount', width: 88, render: (v: number, c) => (c.status === 'SENDING' ? <Progress percent={Math.round((c.sentCount / Math.max(v, 1)) * 100)} size="small" /> : v || '—') },
          { title: 'Delivered', key: 'd', width: 82, render: (_, c) => (c.sentCount ? pct(c.deliveredCount, c.sentCount) : '—') },
          { title: 'Opened', key: 'o', width: 72, render: (_, c) => (c.sentCount ? pct(c.openedCount, c.sentCount) : '—') },
          { title: 'Clicked', key: 'c', width: 70, render: (_, c) => (c.sentCount ? pct(c.clickedCount, c.sentCount) : '—') },
          {
            title: 'When',
            key: 'when',
            width: 120,
            render: (_, c) => {
              const [label, at] = c.completedAt ? ['Sent', c.completedAt] : c.scheduledAt ? ['Scheduled', c.scheduledAt] : ['Edited', c.updatedAt];
              return <Typography.Text type="secondary" style={{ fontSize: 12, lineHeight: 1.4 }}>{label}<br />{fmtDateTime(at)}</Typography.Text>;
            },
          },
          {
            key: 'actions',
            align: 'right',
            width: 42,
            render: (_, c) => (
              <Dropdown
                trigger={['click']}
                placement="bottomRight"
                menu={{
                  items: [
                    { key: 'rename', icon: <EditOutlined />, label: 'Rename' },
                    { key: 'duplicate', icon: <CopyOutlined />, label: 'Duplicate', disabled: duplicate.isPending },
                    ...(c.status !== 'SENDING' ? [{ type: 'divider' as const }, { key: 'delete', icon: <DeleteOutlined />, label: 'Delete', danger: true }] : []),
                  ],
                  onClick: ({ key, domEvent }) => {
                    domEvent.stopPropagation();
                    if (key === 'rename') setRenaming({ id: c.id, name: c.name });
                    if (key === 'duplicate') duplicate.mutateAsync(c.id).then((d) => navigate(`/campaigns/${d.id}`)).catch((e) => message.error(errorMessage(e)));
                    if (key === 'delete')
                      modal.confirm({
                        title: `Delete campaign "${c.name}"?`,
                        content: 'Its recipients and stats are removed too. This cannot be undone.',
                        okText: 'Delete',
                        okButtonProps: { danger: true },
                        onOk: () => remove.mutateAsync(c.id).then(() => message.success('Campaign deleted')).catch((e) => message.error(errorMessage(e))),
                      });
                  },
                }}
              >
                <Button type="text" size="small" icon={<EllipsisOutlined style={{ fontSize: 18 }} />} aria-label={`Actions for ${c.name}`} loading={duplicate.isPending && duplicate.variables === c.id} onClick={(e) => e.stopPropagation()} />
              </Dropdown>
            ),
          },
        ]}
      />
      </Card>
      <Modal title="Rename campaign" open={!!renaming} onCancel={() => setRenaming(null)} okText="Rename" okButtonProps={{ disabled: !renaming?.name.trim() }} confirmLoading={update.isPending} onOk={saveName} destroyOnHidden>
        <Input aria-label="Campaign name" autoFocus maxLength={160} value={renaming?.name ?? ''} onChange={(e) => setRenaming((r) => (r ? { ...r, name: e.target.value } : r))} onPressEnter={saveName} />
      </Modal>
    </div>
  );
}
